// HR > SOP Calendar (Phase 2). Dated SOP tasks materialized from the 35 recurring
// templates. Month switcher, owner/status filters, done toggle + note + reassign,
// KPI tiles, and a manual "Generate this month" fallback to the cron.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { HRC, fmtDate, Card, SectionTitle, Th, Td, Table, Btn, Stat, input, MonthPicker, Empty, Loading } from './hrkit';

const OWNERS = ['Aditi', 'Girija'];

function StatusPill({ t }) {
  const overdue = t.overdue;
  const c = t.status === 'done' ? HRC.green : overdue ? HRC.red : HRC.amber;
  const label = t.status === 'done' ? 'Done' : overdue ? 'OVERDUE' : 'Pending';
  return <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    color: c, background: c + '1A', whiteSpace: 'nowrap' }}>{label}</span>;
}

export default function SopCalendar() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [owner, setOwner] = useState('');
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genMsg, setGenMsg] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = { month };
    if (owner) params.owner = owner;
    if (status) params.status = status;
    api.get('/hr/sop/tasks', { params }).then(r => setRows(r.data.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [month, owner, status]);
  useEffect(() => { load(); }, [load]);

  const generate = () => {
    setGenMsg('');
    api.post('/hr/sop/tasks/generate', { month })
      .then(r => { setGenMsg(`Generated ${r.data.data.created} new task(s).`); load(); })
      .catch(e => setGenMsg(e.response?.data?.error || 'Generation failed'));
  };

  const patch = (id, body, optimistic) => {
    if (optimistic) setRows(rs => rs.map(r => r.id === id ? { ...r, ...optimistic } : r));
    api.patch(`/hr/sop/tasks/${id}`, body).then(load).catch(() => load());
  };
  const toggle = (t) => patch(t.id, { status: t.status === 'done' ? 'pending' : 'done' },
    { status: t.status === 'done' ? 'pending' : 'done', overdue: t.status === 'done' ? t.overdue : false });

  const kpi = {
    total: rows.length,
    done: rows.filter(r => r.status === 'done').length,
    pending: rows.filter(r => r.status === 'pending').length,
    overdue: rows.filter(r => r.overdue).length,
  };
  const sel = { ...input, width: 'auto', minWidth: 130 };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 16 }}>
        <Stat label="Tasks This Month" value={kpi.total} />
        <Stat label="Done" value={kpi.done} accent={HRC.green} />
        <Stat label="Pending" value={kpi.pending} accent={HRC.amber} />
        <Stat label="Overdue" value={kpi.overdue} accent={HRC.red} />
      </div>

      <Card>
        <SectionTitle right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {genMsg && <span style={{ fontSize: 12, color: HRC.muted }}>{genMsg}</span>}
            <Btn variant="soft" onClick={generate}>⚙ Generate this month</Btn>
          </div>
        }>SOP Calendar</SectionTitle>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
          <MonthPicker value={month} onChange={setMonth} />
          <select value={owner} onChange={e => setOwner(e.target.value)} style={sel}>
            <option value="">All owners</option>
            {OWNERS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value)} style={sel}>
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="done">Done</option>
          </select>
          {(owner || status) && <Btn variant="ghost" size="sm" onClick={() => { setOwner(''); setStatus(''); }}>Reset</Btn>}
        </div>

        {loading ? <Loading /> : rows.length === 0 ? (
          <Empty>No SOP tasks for {month}. Use “Generate this month” to materialize them.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th style={{ width: 36 }}></Th>
                <Th>Due</Th><Th>Particulars</Th><Th>Owner</Th><Th>For</Th><Th>Status</Th><Th>Note</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(t => (
                <tr key={t.id} style={t.overdue ? { background: '#FDF3F3' } : undefined}>
                  <Td><input type="checkbox" checked={t.status === 'done'} onChange={() => toggle(t)} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: HRC.green }} /></Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.due_date)}</Td>
                  <Td style={{ textDecoration: t.status === 'done' ? 'line-through' : 'none', color: t.status === 'done' ? HRC.muted : HRC.text }}>{t.particulars}</Td>
                  <Td>
                    <select value={t.owner || ''} onChange={e => patch(t.id, { owner: e.target.value })}
                      style={{ ...input, width: 'auto', minWidth: 90, padding: '4px 6px', fontSize: 12 }}>
                      <option value="">—</option>
                      {OWNERS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Td>
                  <Td style={{ fontSize: 12, color: HRC.muted, whiteSpace: 'nowrap' }}>{t.employee_name ? `${t.employee_name}${t.employee_code ? ` (${t.employee_code})` : ''}` : '—'}</Td>
                  <Td><StatusPill t={t} /></Td>
                  <Td>
                    <input defaultValue={t.note || ''} placeholder="note…" onBlur={e => { if (e.target.value !== (t.note || '')) patch(t.id, { note: e.target.value }); }}
                      style={{ ...input, padding: '5px 8px', fontSize: 12, minWidth: 120 }} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
